/**
 * After a LearnDash/WP Pro Quiz is completed in the browser, inserts a certificate
 * request link into `.ld-quiz-actions` when the score is above 50% and the quiz is
 * the last quiz in the current course, or a flagged kennistest quiz.
 */

const POINTS_SEL = '.wpProQuiz_points';
const ATTR_DONE = 'data-alloy-cert-done';

function parsePercentFromRoot(root) {
  const el = root.querySelector(POINTS_SEL);
  if (!el) {
    return null;
  }
  const text = el.textContent || '';
  const m = text.match(/(\d+(?:[.,]\d+)?)\s*%/);
  if (!m) {
    return null;
  }
  return parseFloat(m[1].replace(',', '.'));
}

function getQuizPostIdFromRoot(root) {
  const quizContent = root.classList?.contains('wpProQuiz_content')
    ? root
    : root.querySelector('.wpProQuiz_content');

  if (!quizContent) {
    return null;
  }

  const meta = quizContent.getAttribute('data-quiz-meta');
  if (!meta) {
    return null;
  }

  try {
    const parsed = JSON.parse(meta);
    const quizPostId = Number(parsed?.quiz_post_id);

    return Number.isFinite(quizPostId) && quizPostId > 0 ? quizPostId : null;
  } catch {
    return null;
  }
}

function hideContinueLinks(root) {
  root.querySelectorAll('#quiz_continue_link, .quiz_continue_link').forEach((el) => {
    el.hidden = true;
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
  });
}

function observeQuizActions(scan) {
  scan();

  const observer = new MutationObserver(() => {
    scan();
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function initKennistestCertificateButton(cfg) {
  const baseUrl = cfg.certificateRequestUrl;
  const quizTitleRaw = cfg.quizTitle;
  const kennistestQuizId = Number(cfg.kennistestQuizId);
  const label =
    typeof cfg.certificateButtonLabel === 'string' && cfg.certificateButtonLabel
      ? cfg.certificateButtonLabel
      : 'Certificaat aanvragen';

  if (
    !baseUrl ||
    typeof quizTitleRaw !== 'string' ||
    !Number.isFinite(kennistestQuizId) ||
    kennistestQuizId <= 0
  ) {
    return;
  }

  function buildHref() {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('title', quizTitleRaw);
    return url.toString();
  }

  function isKennistestQuiz(root) {
    const quizPostId = getQuizPostIdFromRoot(root);
    return quizPostId !== null && quizPostId === kennistestQuizId;
  }

  function processQuizRoot(root) {
    if (isKennistestQuiz(root)) {
      hideContinueLinks(root);
    }
  }

  function processActionsEl(actions) {
    if (actions.getAttribute(ATTR_DONE)) {
      return;
    }

    const root =
      actions.closest('.wpProQuiz_content') || actions.closest('.wpProQuiz_quiz');
    if (!root) {
      return;
    }

    const pointsEl = root.querySelector(POINTS_SEL);
    if (!pointsEl) {
      return;
    }

    const pct = parsePercentFromRoot(root);
    if (pct === null) {
      return;
    }

    actions.setAttribute(ATTR_DONE, '1');

    if (!isKennistestQuiz(root)) {
      return;
    }

    hideContinueLinks(root);

    if (pct <= 50) {
      return;
    }

    const a = document.createElement('a');
    a.className = 'ld-button alloy-cert-request-link alloy-cert-request-link--kennistest';
    a.href = buildHref();
    a.textContent = label;
    actions.appendChild(a);
  }

  function scan() {
    document.querySelectorAll('.ld-quiz-actions').forEach(processActionsEl);
    document.querySelectorAll('.wpProQuiz_content, .wpProQuiz_quiz').forEach(processQuizRoot);
  }

  observeQuizActions(scan);
}

function initCourseCertificateButton(cfg) {
  if (
    !cfg.certificateRequestUrl ||
    typeof cfg.elearningName !== 'string' ||
    !cfg.lastCourseQuizId
  ) {
    return;
  }

  const baseUrl = cfg.certificateRequestUrl;
  const elearningRaw = cfg.elearningName;
  const lastCourseQuizId = Number(cfg.lastCourseQuizId);
  const label =
    typeof cfg.certificateButtonLabel === 'string' && cfg.certificateButtonLabel
      ? cfg.certificateButtonLabel
      : 'Certificaat aanvragen';

  function buildHref() {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('elearning', elearningRaw);
    return url.toString();
  }

  function isLastCourseQuiz(root) {
    const quizPostId = getQuizPostIdFromRoot(root);
    return quizPostId !== null && quizPostId === lastCourseQuizId;
  }

  function hideContinueLinkIfLastQuiz(root) {
    if (!isLastCourseQuiz(root)) {
      return;
    }
    hideContinueLinks(root);
  }

  function processQuizRoot(root) {
    hideContinueLinkIfLastQuiz(root);
  }

  function processActionsEl(actions) {
    if (actions.getAttribute(ATTR_DONE)) {
      return;
    }

    const root =
      actions.closest('.wpProQuiz_content') || actions.closest('.wpProQuiz_quiz');
    if (!root) {
      return;
    }

    const pointsEl = root.querySelector(POINTS_SEL);
    if (!pointsEl) {
      return;
    }

    const pct = parsePercentFromRoot(root);
    if (pct === null) {
      return;
    }

    actions.setAttribute(ATTR_DONE, '1');

    hideContinueLinkIfLastQuiz(root);

    if (pct <= 50) {
      return;
    }

    if (!isLastCourseQuiz(root)) {
      return;
    }

    const a = document.createElement('a');
    a.className = 'ld-button alloy-cert-request-link';
    a.href = buildHref();
    a.textContent = label;
    actions.appendChild(a);
  }

  function scan() {
    document.querySelectorAll('.ld-quiz-actions').forEach(processActionsEl);
    document.querySelectorAll('.wpProQuiz_content, .wpProQuiz_quiz').forEach(processQuizRoot);
  }

  observeQuizActions(scan);
}

export default function learndashCertificateButton() {
  const cfg =
    typeof window !== 'undefined' &&
      window.alloyLdCertButton &&
      typeof window.alloyLdCertButton === 'object'
      ? window.alloyLdCertButton
      : null;

  if (!cfg || !cfg.loadCertificateButton) {
    return;
  }

  if (cfg.isKennistest) {
    initKennistestCertificateButton(cfg);
    return;
  }

  initCourseCertificateButton(cfg);
}
